import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";

// internal deps
import { CartOverlaysContext, GlobalHeaderState, SwgyHeaderKeysList } from "@swiggy-private/checkout-core";
import { CroutonPriority, CroutonIds } from "@swiggy-private/cart-types";

// components
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// types
import { CappingModes, ICBCCNudge } from "./types";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";

// constants
import {
    CART_CBCC_REMIND_ME_NUDGE_CLICK,
    CROUTON_CSS_PROPERTIES,
    CROUTON_DURATION,
    ERROR_ICON_ID,
    appendDiscount,
} from "./constants";
import { CART_CBCC_NUDGE_TEST_IDS } from "@swiggy-private/cart-test-ids";

// styles
import styles from "./styles/CbccNudge.scss";
import {
    BellIconWrapper,
    NudgeContainer,
    NudgeCtaWrapper,
    NudgeImageWrapper,
    NudgePostInterestHeadingWrapper,
    NudgePostInterestTextWrapper,
    ShimmerCta,
    ShimmerRow1,
    ShimmerRow2,
    AnimatedBorder,
    ContentWrapper,
    CardImage,
} from "./styles/CbccNudge.styles";

const CbccNudge = ({
    setCroutonVisibility,
    itemData,
    applicableDiscount,
    recordCbccInterest,
    impressionCbccNudge,
    clickCbccNudge,
    impressionCbccThankyouCrouton,
    impressionCbccResponseReceived,
    updateFrequencyCount,
    logClevertapEvent,
}: ICBCCNudge): React.ReactElement => {
    const [loading, setLoading] = useState(false);
    const [responseReceived, setResponseReceived] = useState(false);
    const [croutonData, setCroutonData] = useState({ icon: "", text: "" });
    const bannerData = itemData?.meta?.banners?.[0];
    const widgetData = bannerData?.widgetData;
    const bannerId = bannerData?.bannerId;

    const { registerCrouton } = useContext(CartOverlaysContext);

    const showErrorCrouton = useCallback(() => {
        setCroutonData({ icon: ERROR_ICON_ID, text: widgetData.croutonErrorText });
    }, [widgetData.croutonErrorText]);

    const showSuccessCrouton = useCallback(() => {
        impressionCbccThankyouCrouton(bannerId);
        setCroutonData({ icon: widgetData.postClickCheckIconId, text: widgetData.croutonSuccessText });
    }, [widgetData.croutonSuccessText, widgetData.postClickCheckIconId, impressionCbccThankyouCrouton, bannerId]);

    const logClickOnClevertap = useCallback(() => {
        const userId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.userId) || "NULL";

        const clevertapParams = {
            userId,
        };

        logClevertapEvent(CART_CBCC_REMIND_ME_NUDGE_CLICK, clevertapParams);
    }, [logClevertapEvent]);

    const handleRemindMeLater = useCallback(async () => {
        clickCbccNudge(`${applicableDiscount}`, bannerId);
        logClickOnClevertap();
        setLoading(true);
        try {
            const res = await recordCbccInterest?.();
            if (res?.statusCode === 0) {
                showSuccessCrouton();
                setResponseReceived(true);
                impressionCbccResponseReceived(bannerId);
            } else {
                showErrorCrouton();
            }
            setLoading(false);
        } catch (err) {
            showErrorCrouton();
            setLoading(false);
        }
    }, [
        applicableDiscount,
        bannerId,
        clickCbccNudge,
        impressionCbccResponseReceived,
        logClickOnClevertap,
        recordCbccInterest,
        showErrorCrouton,
        showSuccessCrouton,
    ]);

    const Content = useCallback(() => {
        if (loading) {
            return (
                <div data-testid={CART_CBCC_NUDGE_TEST_IDS.LOADER}>
                    <ShimmerCta />
                    <ShimmerRow1 />
                    <ShimmerRow2 />
                </div>
            );
        } else if (responseReceived) {
            return (
                <React.Fragment>
                    <NudgePostInterestHeadingWrapper>
                        <Image cloudinaryId={widgetData.postClickCheckIconId} width="12px" height="12px" />
                        <Text
                            data-testid={CART_CBCC_NUDGE_TEST_IDS.POST_CLICK_TEXT}
                            variant="V2.body3.neutral.bold"
                            color="positive"
                            dangerouslySetInnerHTML={{ __html: widgetData.postClickSuccessText }}
                        />
                    </NudgePostInterestHeadingWrapper>
                    <NudgePostInterestTextWrapper>
                        <Text
                            data-testid={CART_CBCC_NUDGE_TEST_IDS.POST_CLICK_HEADING}
                            variant="V2.body3.neutral.medium"
                            color="text_High_Emphasis"
                            dangerouslySetInnerHTML={{
                                __html: widgetData.postClickHeading,
                            }}
                        />
                    </NudgePostInterestTextWrapper>
                </React.Fragment>
            );
        }
        return (
            <React.Fragment>
                <AnimatedBorder></AnimatedBorder>
                <ContentWrapper>
                    <Text
                        data-testid={CART_CBCC_NUDGE_TEST_IDS.PRE_CLICK_HEADING}
                        variant="V2.body1.neutral.regular"
                        color="text_High_Emphasis"
                        dangerouslySetInnerHTML={{
                            __html: appendDiscount(widgetData.preClickHeading, applicableDiscount),
                        }}
                    />
                    <NudgeCtaWrapper data-testid={CART_CBCC_NUDGE_TEST_IDS.PRE_CLICK_CTA} onClick={handleRemindMeLater}>
                        <Text
                            variant="V2.body3.neutral.medium"
                            color="text_inverse_Highest_Emphasis"
                            dangerouslySetInnerHTML={{ __html: widgetData.preClickCtaText }}
                        />
                        <BellIconWrapper>
                            <Image width="100%" height="100%" cloudinaryId={widgetData.preClickBellIconId} />
                        </BellIconWrapper>
                    </NudgeCtaWrapper>
                </ContentWrapper>
            </React.Fragment>
        );
    }, [
        loading,
        responseReceived,
        widgetData.preClickHeading,
        applicableDiscount,
        handleRemindMeLater,
        widgetData.preClickCtaText,
        widgetData.preClickBellIconId,
        widgetData.postClickCheckIconId,
        widgetData.postClickSuccessText,
        widgetData.postClickHeading,
    ]);

    useEffect(() => {
        impressionCbccNudge(`${applicableDiscount}`, bannerId);
    }, [impressionCbccNudge, applicableDiscount, bannerId]);

    useEffect(() => {
        return () => {
            setCroutonData({ icon: "", text: "" });
        };
    }, []);

    useEffect(() => {
        if (!isEmpty(bannerId) && !isEmpty(widgetData.entityType)) {
            updateFrequencyCount(CappingModes.VIEW, bannerId, widgetData.entityType);
        }
    }, [bannerId, updateFrequencyCount, widgetData.entityType]);

    const croutonContent = useMemo(
        () => ({
            title: croutonData.text,
            icon: croutonData.icon,
            cssProperties: CROUTON_CSS_PROPERTIES,
            titleClassName: styles.nudgeCroutonTitle,
            setCroutonVisibility: setCroutonVisibility,
            durationOfCrouton: CROUTON_DURATION,
        }),
        [croutonData.icon, croutonData.text, setCroutonVisibility],
    );

    useEffect(() => {
        if (!isEmpty(croutonData.text)) {
            registerCrouton({
                content: croutonContent,
                id: CroutonIds.CBCC_NUDGE_CROUTON,
                priority: CroutonPriority.PRIORITY_ORDER_ONE,
            });
        }
    }, [croutonContent, croutonData.text, registerCrouton]);

    if (isEmpty(bannerData) || isEmpty(widgetData)) {
        return <></>;
    }

    return (
        <NudgeContainer
            gradientBg={!loading && !responseReceived}
            data-testid={CART_CBCC_NUDGE_TEST_IDS.NUDGE_CONTAINER}
        >
            <Content />
            <NudgeImageWrapper data-testid={CART_CBCC_NUDGE_TEST_IDS.CARD_IMAGE}>
                <CardImage cloudinaryId={widgetData.cardImageId} />
            </NudgeImageWrapper>
        </NudgeContainer>
    );
};

export default React.memo(CbccNudge);
